import {createContext, useState, useCallback, useContext, useMemo} from "react";
import CognitoAuthenticationProvider from "./CognitoAuthenticationProvider";
// import apiFetch from "@wordpress/api-fetch";
// import {useNoticeContext} from "../NoticeContext";
// import handleApiError from '../../utils/handleApiError';
import {useCustomerDataContext} from "../CustomerDataContext";
// import {dispatch} from "../../store";

const AuthenticationContext = createContext({});

export const AuthenticationProvider = (
    {
        provider,
        children
    }) => {
    const [state, setState] = useState({
        loginScreen: false
    });

    const [userState, setUserState] = useState({});
    const {
        fetchCustomer: fetchUser,
        email,
        firstName,
        lastName,
        mfa,
        billingAddress,
        billingEmail,
        clearCustomerData
    } = useCustomerDataContext();

    const [status, setStatus] = useState({
        loggingIn: false,
        loggingOut: false,
        initialized: false
    });

    const setUserAttribute = useCallback((key, value) => {
        setUserState(prevState => ({...prevState, [key]: value}));
    }, []);

    const customer = useMemo(() => {
        return {
            setProps(props) {
                setUserState(prevState => ({...prevState, ...props}));
            },
            setAttribute: setUserAttribute,
            setUsername(username) {
                setUserAttribute('username', username);
            },
            setEmail(email) {
                setUserAttribute('email', email);
            },
            setBillingEmail(email) {
                setUserAttribute('billingEmail', email);
            },
            showMFA() {
                return !!userState.canShowMFA;
            },
            setShowMFA(value) {
                setUserAttribute('canShowMFA', value);
            },
            setLoggedIn(value) {
                setUserAttribute('loggedIn', value);
            },
            isLoggedIn() {
                return !!userState?.loggedIn;
            },
            logout() {
                this.setLoggedIn(false);
                clearCustomerData();
            },
            setNeedsPasswordChange(value) {
                setUserAttribute('passwordChangeRequired', value);
            },
            getUsername() {
                return userState?.username;
            },
            getEmail() {
                return email;
            },
            getBillingEmail() {
                return billingEmail;
            },
            getFirstName() {
                return firstName;
            },
            getLastName() {
                return lastName;
            },
            getMfa() {
                return mfa;
            },
            needsPasswordChange() {
                return !!userState?.passwordChangeRequired;
            }
        }
    }, [
        email,
        firstName,
        lastName,
        billingEmail,
        billingAddress,
        userState,
    ]);

    const statusActions = useMemo(() => {
        return {
            setLoggingIn(bool) {
                setStatus(prevState => ({...prevState, loggingIn: bool}));
            },
            setLoggingOut(bool) {
                setStatus(prevState => ({...prevState, loggingOut: bool}));
            },
            setInitialized(bool) {
                setStatus(prevState => ({...prevState, initialized: bool}));
            }
        }
    }, []);

    const Authentication = getProvider(provider);
    const showLoginScreen = useCallback(() => {
        return setState(prevState => ({
            ...
                prevState, loginScreen: true
        }));
    }, []);
    const hideLoginScreen = useCallback(() => {
        return setState(prevState => ({
            ...
                prevState, loginScreen: false
        }));
    }, []);

    const fetchCustomer = useCallback(() => {
        fetchUser().then()
    }, [fetchUser]);

    const context = {
        customer,
        ...state,
        actions: {
            showLoginScreen,
            hideLoginScreen
        },
        status,
        statusActions,
        fetchCustomer
    }
    return (
        <Authentication
            Provider={AuthenticationContext.Provider}
            {...context}
        >
            {children}
        </Authentication>
    )
}

const getProvider = (provider) => {
    switch (provider) {
        case 'cognito':
            return CognitoAuthenticationProvider;
    }
}

export const useAuthenticationContext = () => useContext(AuthenticationContext);
